import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  Paper,
} from "@mui/material";
import { Edit, InfoOutlined, Add, Search } from "@mui/icons-material";
import { useState, useCallback } from "react";
import { isSameDay, format, isValid, isYesterday, parseISO } from "date-fns";
import { toast } from "react-toastify";
import DateTimePicker from "./date-time-picker";
import CustomSelect from "src/components/custom-select";
import AddressAutocomplete from "./shipper-autocomplete";
import CommoditiesTable from "./CommoditiesTable";
import SingleOrderDetails from "./single-order-details";
import LegsTable from "./order-legs";

const TIME_OPTIONS = [
  "08:00",
  "08:15",
  "08:30",
  "08:45",
  "09:00",
  "09:15",
  "09:30",
  "09:45",
  "10:00",
  "10:15",
  "10:30",
  "10:45",
  "11:00",
  "11:15",
  "11:30",
  "11:45",
  "12:00",
  "12:15",
  "12:30",
  "12:45",
  "13:00",
  "13:15",
  "13:30",
  "13:45",
  "14:00",
  "14:15",
  "14:30",
  "14:45",
  "15:00",
  "15:15",
  "15:30",
  "15:45",
  "16:00",
  "16:15",
  "16:30",
  "16:45",
  "17:00",
  "17:15",
  "17:30",
  "17:45",
  "18:00",
  "18:15",
  "18:30",
  "18:45",
  "19:00",
  "19:15",
  "19:30",
  "19:45",
  "20:00",
  "20:15",
  "20:30",
  "20:45",
];

export default function Stop({
  values,
  setFieldValue,
  setTouched,
  detailsRef,
  commoditiesRef,
  shipperCustomers,
  consigneeCustomers,
  billingCustomers,
  accessorialsData,
  orderId,
  touched,
  errors,
  isNew = false,
  stopData,
  stopIndex,
  stops,
  disabledView,
  type,
}) {
  // State management
  const [showAccessorialDropdown, setShowAccessorialDropdown] = useState(false);
  const [accessorialSearchTerm, setAccessorialSearchTerm] = useState("");
  const [openBillingModal, setOpenBillingModal] = useState(false);
  const [showBillingForm, setShowBillingForm] = useState(false);

  // Data access utilities
  const getNestedValue = useCallback((obj, path) => {
    if (!path || !obj) return undefined;
    return path.split(".").reduce((current, key) => current?.[key], obj);
  }, []);

  const safeFormat = (dateValue) => {
    if (dateValue && isValid(new Date(dateValue))) {
      return format(new Date(dateValue), "MM/dd/yyyy hh:mm a");
    }
    return "";
  };

  const handleNestedChange = useCallback(
    (section, field, value) => {
      if (section === "shipper") {
        // If this order uses a single shipper at the order level, write to top-level `shipper`
        if (values?.orderType === "single_shipper") {
          setFieldValue(`shipper.${field}`, value);
        } else {
          setFieldValue(`orderStops[${stopIndex}].${section}.${field}`, value);
        }
      } else {
        setFieldValue(`orderStops[${stopIndex}].${section}.${field}`, value);
      }
    },
    [setFieldValue, stopIndex],
  );

  const handleAddressChange = useCallback(
    (section, field, value) => {
      if (section === "shipper") {
        // If this order uses a single shipper at the order level, write to top-level `shipper.addresses`
        if (values?.orderType === "single_shipper") {
          setFieldValue(`shipper.addresses.${field}`, value);
        } else {
          setFieldValue(
            `orderStops[${stopIndex}].${section}.addresses.${field}`,
            value,
          );
        }
      } else {
        setFieldValue(
          `orderStops[${stopIndex}].${section}.addresses.${field}`,
          value,
        );
      }
    },
    [setFieldValue, stopIndex],
  );

  const handleStopChange = useCallback(
    (field, value) => {
      setFieldValue(`orderStops[${stopIndex}].${field}`, value);
    },
    [setFieldValue, stopIndex],
  );

  const getFieldValue = useCallback(
    (fieldPath, section) => {
      if (section) {
        const primaryValue = getNestedValue(
          values?.orderStops?.[stopIndex]?.[section],
          fieldPath,
        );
        if (primaryValue !== undefined && primaryValue !== null)
          return primaryValue;
        const sectionMap = {
          consignee: "consigneeAddress",
          billingParty: "billingPartyAddress",
        };
        const fallbackSection = sectionMap[section];
        if (fallbackSection) {
          const fallbackValue =
            getNestedValue(stops?.[stopIndex]?.[fallbackSection], fieldPath) ||
            getNestedValue(
              stops?.[stopIndex]?.[fallbackSection]?.address,
              fieldPath,
            );
          if (fallbackValue !== undefined && fallbackValue !== null)
            return fallbackValue;
        }
      } else {
        const primaryValue = getNestedValue(values, fieldPath);
        if (primaryValue !== undefined && primaryValue !== null)
          return primaryValue;
        const fallbackValue = getNestedValue(stops?.[stopIndex], fieldPath);
        if (fallbackValue !== undefined && fallbackValue !== null)
          return fallbackValue;
      }
      return "";
    },
    [values, stops, stopIndex, getNestedValue],
  );

  // Dynamic data getters based on orderType
  const getShipperSectionData = useCallback(
    (fieldPath) => {
      if (values?.orderType === "single_consignee") {
        // For single_consignee, show consignee data in shipper section
        const primaryValue = getNestedValue(
          values?.orderStops?.[stopIndex]?.consignee,
          fieldPath,
        );
        if (primaryValue !== undefined && primaryValue !== null)
          return primaryValue;
        const fallbackValue =
          getNestedValue(stops?.[stopIndex]?.consigneeAddress, fieldPath) ||
          getNestedValue(
            stops?.[stopIndex]?.consigneeAddress?.address,
            fieldPath,
          ) ||
          getNestedValue(
            stops?.[stopIndex]?.consigneeAddress?.addresses,
            fieldPath,
          );
        return fallbackValue !== undefined && fallbackValue !== null
          ? fallbackValue
          : "";
      } else if (values?.orderType === "sequence") {
        // For sequence, show shipper data from orderStops
        const primaryValue = getNestedValue(
          values?.orderStops?.[stopIndex]?.shipper,
          fieldPath,
        );
        if (
          primaryValue !== undefined &&
          primaryValue !== null &&
          primaryValue !== ""
        )
          return primaryValue;

        // Then try from values.shipper
        const shipperValue = getNestedValue(values?.shipper, fieldPath);
        if (
          shipperValue !== undefined &&
          shipperValue !== null &&
          shipperValue !== ""
        )
          return shipperValue;

        // Finally try fallback from stops array
        const fallbackValue =
          getNestedValue(stops?.[stopIndex]?.shipper, fieldPath) ||
          getNestedValue(stops?.[stopIndex]?.shipperAddress, fieldPath) ||
          getNestedValue(
            stops?.[stopIndex]?.shipperAddress?.address,
            fieldPath,
          ) ||
          getNestedValue(
            stops?.[stopIndex]?.shipperAddress?.addresses,
            fieldPath,
          );
        return fallbackValue !== undefined &&
          fallbackValue !== null &&
          fallbackValue !== ""
          ? fallbackValue
          : "";
      } else {
        // For other types, show shipper data
        const primaryValue = getNestedValue(values?.shipper, fieldPath);
        if (
          primaryValue !== undefined &&
          primaryValue !== null &&
          primaryValue !== ""
        )
          return primaryValue;
        const fallbackValue =
          getNestedValue(stops?.[stopIndex]?.shipperAddress, fieldPath) ||
          getNestedValue(
            stops?.[stopIndex]?.shipperAddress?.addresses,
            fieldPath,
          ) ||
          getNestedValue(
            stops?.[stopIndex]?.shipperAddress?.addresses,
            fieldPath,
          );
        return fallbackValue !== undefined &&
          fallbackValue !== null &&
          fallbackValue !== ""
          ? fallbackValue
          : "";
      }
    },
    [values, stops, stopIndex, getNestedValue],
  );

  const getConsigneeSectionData = useCallback(
    (fieldPath) => {
      if (values?.orderType === "single_consignee") {
        // For single_consignee, show shipper data in consignee section
        // First try from values.shipper (but skip if empty)
        const primaryValue = getNestedValue(values?.shipper, fieldPath);
        if (
          primaryValue !== undefined &&
          primaryValue !== null &&
          primaryValue !== ""
        )
          return primaryValue;

        // Then try from orderStops shipper data
        const shipperStopValue = getNestedValue(
          values?.orderStops?.[stopIndex]?.shipper,
          fieldPath,
        );
        if (
          shipperStopValue !== undefined &&
          shipperStopValue !== null &&
          shipperStopValue !== ""
        )
          return shipperStopValue;

        // Finally try fallback from stops array
        const fallbackValue =
          getNestedValue(stops?.[stopIndex]?.shipper, fieldPath) ||
          getNestedValue(stops?.[stopIndex]?.shipperAddress, fieldPath) ||
          getNestedValue(
            stops?.[stopIndex]?.shipperAddress?.address,
            fieldPath,
          ) ||
          getNestedValue(
            stops?.[stopIndex]?.shipperAddress?.addresses,
            fieldPath,
          );
        return fallbackValue !== undefined &&
          fallbackValue !== null &&
          fallbackValue !== ""
          ? fallbackValue
          : "";
      } else {
        // For other types (including point_to_point), show consignee data
        // First try to get from consignee object
        const primaryValue = getNestedValue(
          values?.orderStops?.[stopIndex]?.consignee,
          fieldPath,
        );

        if (
          primaryValue !== undefined &&
          primaryValue !== null &&
          primaryValue !== ""
        ) {
          return primaryValue;
        }

        // Then try consigneeAddress object
        const secondaryValue = getNestedValue(
          values?.orderStops?.[stopIndex]?.consigneeAddress,
          fieldPath,
        );

        if (
          secondaryValue !== undefined &&
          secondaryValue !== null &&
          secondaryValue !== ""
        ) {
          return secondaryValue;
        }

        // Then try nested address object
        const nestedAddressValue = getNestedValue(
          values?.orderStops?.[stopIndex]?.consigneeAddress?.address,
          fieldPath,
        );

        if (
          nestedAddressValue !== undefined &&
          nestedAddressValue !== null &&
          nestedAddressValue !== ""
        ) {
          return nestedAddressValue;
        }

        // Finally try the stops array
        const stopsValue =
          getNestedValue(stops?.[stopIndex]?.consignee, fieldPath) ||
          getNestedValue(stops?.[stopIndex]?.consigneeAddress, fieldPath) ||
          getNestedValue(
            stops?.[stopIndex]?.consigneeAddress?.address,
            fieldPath,
          );

        return stopsValue !== undefined &&
          stopsValue !== null &&
          stopsValue !== ""
          ? stopsValue
          : "";
      }
    },
    [values, stops, stopIndex, getNestedValue],
  );

  const getBillingPartyValue = useCallback(
    (fieldPath) => {
      const primaryValue = getNestedValue(values?.billingParty, fieldPath);
      if (primaryValue !== undefined && primaryValue !== null)
        return primaryValue;
      const fallbackValue =
        getNestedValue(stops?.[stopIndex]?.billingPartyAddress, fieldPath) ||
        getNestedValue(
          stops?.[stopIndex]?.billingPartyAddress?.address,
          fieldPath,
        );
      return fallbackValue !== undefined && fallbackValue !== null
        ? fallbackValue
        : "";
    },
    [values, stops, stopIndex, getNestedValue],
  );

  const getStopValue = useCallback(
    (fieldPath) => {
      const primaryValue = getNestedValue(
        values?.orderStops?.[stopIndex],
        fieldPath,
      );
      if (primaryValue !== undefined && primaryValue !== null)
        return primaryValue;
      const fallbackValue = getNestedValue(stops?.[stopIndex], fieldPath);
      return fallbackValue !== undefined && fallbackValue !== null
        ? fallbackValue
        : "";
    },
    [values, stops, stopIndex, getNestedValue],
  );

  // If disabledView is true, show the shipping details view
  if (disabledView) {
    return (
      <SingleOrderDetails
        values={values}
        stopData={stopData}
        stopIndex={stopIndex}
        stops={stops}
        getShipperValue={getShipperSectionData}
        getConsigneeValue={getConsigneeSectionData}
        getBillingPartyValue={getBillingPartyValue}
        getStopValue={getStopValue}
      />
    );
  }

  // Event handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    handleStopChange(name, value);
  };

  // Commodity management functions
  const handleCommodityChange = (index, field, value) => {
    const path = `orderStops[${stopIndex}].commodities[${index}]`;
    setFieldValue(`${path}.${field}`, value);
    const currentCommodity =
      values?.orderStops?.[stopIndex]?.commodities?.[index];
    if (
      currentCommodity?.uuid &&
      currentCommodity?._action !== "create" &&
      currentCommodity?._action !== "delete"
    ) {
      setFieldValue(`${path}._action`, "update");
    }
  };

  const addCommodity = () => {
    const newCommodity = {
      descriptions: "FAK",
      quantity: null,
      lengthValue: null,
      width: null,
      height: null,
      pieces: null,
      lf: null,
      weight: null,
      class: "",
      typeUuid: "",
      orderUuid: orderId,
      _action: "create",
    };
    const currentCommodities =
      values?.orderStops?.[stopIndex]?.commodities || [];
    setFieldValue(`orderStops[${stopIndex}].commodities`, [
      ...currentCommodities,
      newCommodity,
    ]);
  };

  const removeCommodity = (indexToRemove) => {
    const currentCommodities =
      values?.orderStops?.[stopIndex]?.commodities || [];
    const commodity = currentCommodities[indexToRemove];
    if (!commodity) {
      console.error(`No commodity found at index ${indexToRemove}`);
      return;
    }
    if (commodity.uuid) {
      const updatedCommodities = [...currentCommodities];
      updatedCommodities[indexToRemove] = {
        uuid: commodity.uuid,
        _action: "delete",
      };
      setFieldValue(`orderStops[${stopIndex}].commodities`, updatedCommodities);
    } else {
      const updatedCommodities = currentCommodities.filter(
        (_, index) => index !== indexToRemove,
      );
      setFieldValue(`orderStops[${stopIndex}].commodities`, updatedCommodities);
    }
  };

  // Billing modal handlers
  const handleOpenBillingModal = () => setOpenBillingModal(true);
  const handleCloseBillingModal = () => setOpenBillingModal(false);
  const handleContinueBillingEdit = () => {
    setOpenBillingModal(false);
    setShowBillingForm(true);
  };

  // Unified coordinate extractor
  const getLatLng = (type) => {
    const stopData = stops?.[stopIndex];
    const value = values?.[type];
    const stop = stopData?.[type];
    const stopAddress = stopData?.[`${type}Address`];

    const latitude =
      value?.addresses?.latitude ??
      value?.address?.latitude ??
      stop?.addresses?.latitude ??
      stop?.address?.latitude ??
      stopAddress?.address?.latitude;

    const longitude =
      value?.addresses?.longitude ??
      value?.address?.longitude ??
      stop?.addresses?.longitude ??
      stop?.address?.longitude ??
      stopAddress?.address?.longitude;

    return {
      latitude: Number.parseFloat(latitude),
      longitude: Number.parseFloat(longitude),
    };
  };

  // Coordinate validity check
  const hasValidCoordinates = () => {
    const { latitude: shipperLat, longitude: shipperLng } =
      getLatLng("shipper");
    const { latitude: consigneeLat, longitude: consigneeLng } =
      getLatLng("consignee");

    return (
      shipperLat &&
      shipperLng &&
      consigneeLat &&
      consigneeLng &&
      !isNaN(shipperLat) &&
      !isNaN(shipperLng) &&
      !isNaN(consigneeLat) &&
      !isNaN(consigneeLng)
    );
  };

  // Mapbox Distance Calculation
  const calculateDropOffDistancesMapbox = async (
    apiKey,
    origin,
    destination,
  ) => {
    try {
      if (!apiKey) throw new Error("Mapbox API key is required");
      if (!origin?.longitude || !origin?.latitude)
        throw new Error("Origin coordinates are required");
      if (!destination?.longitude || !destination?.latitude)
        throw new Error("Destination coordinates are required");

      const coordinates = `${origin.longitude},${origin.latitude};${destination.longitude},${destination.latitude}`;
      const response = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/driving/${coordinates}?access_token=${apiKey}&geometries=geojson`,
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Mapbox API error (${response.status}): ${errorText}`);
      }

      const data = await response.json();
      if (!data.routes || data.routes.length === 0) {
        throw new Error("No route found between these locations");
      }

      const distanceInMeters = data.routes[0].distance;
      const distanceInMiles = distanceInMeters / 1609.34;

      return {
        totalDistance: distanceInMiles,
        route: data.routes[0],
        duration: data.routes[0].duration,
        geometry: data.routes[0].geometry,
      };
    } catch (error) {
      console.error("Error in calculateDropOffDistancesMapbox:", error);
      throw error;
    }
  };

  // Main function to calculate and update distance
  const calculateDistance = async () => {
    const stopData = stops?.[stopIndex];

    if (!hasValidCoordinates()) {
      toast.error("Valid shipper and consignee coordinates are required");
      return;
    }

    try {
      const apiKey =
        "sk.eyJ1IjoiY29tcHUtdnNpb24iLCJhIjoiY20wZHU1bW4wMGYwNTJsczE4M2g4dTN6OCJ9.DoDyF9sdK1rGeE2Rc0VFWw";

      let origin, destination;

      if (values?.orderType === "single_consignee") {
        origin = getLatLng("consignee"); // Shipper ←→ Consignee are swapped
        destination = getLatLng("shipper");
      } else {
        origin = getLatLng("shipper");
        destination = getLatLng("consignee");
      }

      const { totalDistance } = await calculateDropOffDistancesMapbox(
        apiKey,
        origin,
        destination,
      );
      const distanceInMiles = Math.round(totalDistance);

      setFieldValue(`orderStops[${stopIndex}].billingMiles`, distanceInMiles);
      toast.success(`Distance calculated: ${distanceInMiles} miles`);
    } catch (error) {
      console.error("Error calculating distance:", error);
      toast.error(
        "Failed to calculate distance. Please check coordinates and try again.",
      );
    }
  };

  // Accessorial management
  const handleRemoveAccessorial = (uuid) => {
    const newAccessorials =
      values?.orderStops?.[stopIndex]?.accessorials?.filter(
        (acc) => acc.uuid !== uuid,
      ) || [];
    setFieldValue(`orderStops[${stopIndex}].accessorials`, newAccessorials);
    // Also update the accessorialUuids array to match
    const newAccessorialUuids = newAccessorials.map((acc) => acc.uuid);
    setFieldValue(
      `orderStops[${stopIndex}].accessorialUuids`,
      newAccessorialUuids,
    );
    const accessorial = accessorialsData?.find((acc) => acc.uuid === uuid);
    if (!accessorial) return;

    if (accessorial?.name?.toLowerCase() === "appointment pickup") {
      const pickupFields = {
        [`orderStops[${stopIndex}].isAppointmentPickup`]: false,
        [`orderStops[${stopIndex}].isAppointmentConfirmPickup`]: false,
        [`orderStops[${stopIndex}].pickUpTimeStart`]: "",
        [`orderStops[${stopIndex}].pickUpTimeEnd`]: "",
        [`orderStops[${stopIndex}].readyTimeStart`]: "",
        [`orderStops[${stopIndex}].readyTimeEnd`]: "",
      };
      Object.entries(pickupFields).forEach(([field, value]) =>
        setFieldValue(field, value),
      );
    }

    if (accessorial?.name?.toLowerCase() === "appointment delivery") {
      const deliveryFields = {
        [`orderStops[${stopIndex}].isAppointmentDelivery`]: false,
        [`orderStops[${stopIndex}].isAppointmentConfirmDelivery`]: false,
        [`orderStops[${stopIndex}].deliveryTimeStart`]: "",
        [`orderStops[${stopIndex}].deliveryTimeEnd`]: "",
        [`orderStops[${stopIndex}].requestedTimeStart`]: "",
        [`orderStops[${stopIndex}].requestedTimeEnd`]: "",
      };
      Object.entries(deliveryFields).forEach(([field, value]) =>
        setFieldValue(field, value),
      );
    }
  };

  const handleAddAccessorial = (accessorial) => {
    const currentAccessorials =
      values?.orderStops?.[stopIndex]?.accessorials || [];
    if (currentAccessorials.some((acc) => acc.uuid === accessorial.uuid))
      return;

    const newAccessorials = [...currentAccessorials, accessorial];
    setFieldValue(`orderStops[${stopIndex}].accessorials`, newAccessorials);
    // Also update the accessorialUuids array to match
    const newAccessorialUuids = newAccessorials.map((acc) => acc.uuid);
    setFieldValue(
      `orderStops[${stopIndex}].accessorialUuids`,
      newAccessorialUuids,
    );

    if (
      accessorial.name?.toLowerCase() === "appointment delivery" &&
      !values.isAppointmentDelivery
    ) {
      const deliveryFields = {
        [`orderStops[${stopIndex}].isAppointmentDelivery`]: true,
        [`orderStops[${stopIndex}].deliveryTimeStart`]: "",
        [`orderStops[${stopIndex}].deliveryTimeEnd`]: "",
        [`orderStops[${stopIndex}].requestedTimeStart`]: "",
        [`orderStops[${stopIndex}].requestedTimeEnd`]: "",
      };
      Object.entries(deliveryFields).forEach(([field, value]) =>
        setFieldValue(field, value),
      );
    }

    if (
      accessorial.name?.toLowerCase() === "appointment pickup" &&
      !values.isAppointmentPickup
    ) {
      const pickupFields = {
        [`orderStops[${stopIndex}].isAppointmentPickup`]: true,
        [`orderStops[${stopIndex}].pickUpTimeStart`]: "",
        [`orderStops[${stopIndex}].pickUpTimeEnd`]: "",
        [`orderStops[${stopIndex}].readyTimeStart`]: "",
        [`orderStops[${stopIndex}].readyTimeEnd`]: "",
      };
      Object.entries(pickupFields).forEach(([field, value]) =>
        setFieldValue(field, value),
      );
    }

    setShowAccessorialDropdown(false);
    setAccessorialSearchTerm("");
  };

  const handleDeliveryAppointmentChange = (isChecked) => {
    const appointmentDeliveryAccessorial = accessorialsData.find(
      (accessorial) =>
        accessorial.name?.toLowerCase() === "appointment delivery",
    );

    if (appointmentDeliveryAccessorial) {
      const appointmentDeliveryUuid = appointmentDeliveryAccessorial.uuid;
      // Use per-stop accessorials, not top-level
      let newAccessorialUuids = [
        ...(values?.orderStops?.[stopIndex]?.accessorialUuids?.filter(
          (id) => id && id !== "",
        ) || []),
      ];

      if (isChecked) {
        if (!newAccessorialUuids.includes(appointmentDeliveryUuid)) {
          newAccessorialUuids.push(appointmentDeliveryUuid);
        }
      } else {
        newAccessorialUuids = newAccessorialUuids.filter(
          (uuid) => uuid !== appointmentDeliveryUuid,
        );
      }

      setFieldValue(
        `orderStops[${stopIndex}].accessorialUuids`,
        Array.from(new Set(newAccessorialUuids)),
      );
    }

    setFieldValue(`orderStops[${stopIndex}].isAppointmentDelivery`, isChecked);
    setFieldValue(
      `orderStops[${stopIndex}].isAppointmentConfirmDelivery`,
      false,
    );

    if (isChecked) {
      setFieldValue(
        `orderStops[${stopIndex}].deliveryTimeStart`,
        values.requestedTimeStart || "",
      );
      setFieldValue(
        `orderStops[${stopIndex}].deliveryTimeEnd`,
        values.requestedTimeEnd || "",
      );
      setFieldValue(`orderStops[${stopIndex}].requestedTimeStart`, "");
      setFieldValue(`orderStops[${stopIndex}].requestedTimeEnd`, "");
    } else {
      setFieldValue(
        `orderStops[${stopIndex}].requestedTimeStart`,
        values.deliveryTimeStart || "",
      );
      setFieldValue(
        `orderStops[${stopIndex}].requestedTimeEnd`,
        values.deliveryTimeEnd || "",
      );
      setFieldValue(`orderStops[${stopIndex}].deliveryTimeStart`, "");
      setFieldValue(`orderStops[${stopIndex}].deliveryTimeEnd`, "");
    }
  };

  const isToday = (date) => isSameDay(date, new Date());

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = parseISO(dateString);
    const timeStr = format(date, "HH:mm");

    if (isToday(date)) {
      return `Today @ ${timeStr}`;
    } else if (isYesterday(date)) {
      return `Yesterday @ ${timeStr}`;
    } else {
      return format(date, "MMM d @ HH:mm");
    }
  };

  const filteredAccessorials =
    accessorialsData?.filter(
      (acc) =>
        acc.name.toLowerCase().includes(accessorialSearchTerm.toLowerCase()) &&
        !values?.orderStops?.[stopIndex]?.accessorials?.some(
          (stopAcc) => stopAcc.uuid === acc.uuid,
        ),
    ) || [];

  // Dynamic section titles and field names based on orderType
  const getShipperSectionTitle = () => {
    return values?.orderType === "single_consignee" ? "Consignee" : "Shipper";
  };

  const getConsigneeSectionTitle = () => {
    return values?.orderType === "single_consignee" ? "Shipper" : "Consignee";
  };

  // Render shipper section (dynamic based on orderType)
  const renderShipperSection = () => (
    <Grid item xs={12} md={4}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {getShipperSectionTitle()}
      </Typography>
      <Box
        sx={{
          mb: 1,
          display: "flex",
          flexDirection: "column",
          my: 2,
          gap: 1,
        }}
      >
        <Typography
          variant="subtitle1"
          component="span"
          sx={{
            fontWeight: "bold",
            textTransform: "uppercase",
            color: "#2b3855",
          }}
        >
          {getShipperSectionData("name") || getShipperSectionData("company")}
        </Typography>
        <Typography
          variant="body2"
          component="span"
          sx={{ fontWeight: 500, color: "#2b3855" }}
        >
          {getShipperSectionData("addresses.address")}
        </Typography>
        <Typography
          variant="body2"
          component="span"
          sx={{ fontWeight: 500, color: "#2b3855" }}
        >
          {`${getShipperSectionData("addresses.city")}, ${getShipperSectionData(
            "addresses.state",
          )}, ${getShipperSectionData("addresses.postal")}`}
        </Typography>
      </Box>
      <Typography variant="body2" component="span" sx={{ fontWeight: "bold" }}>
        {stops?.[stopIndex]?.isAppointmentPickup ? "Pickup Time" : "Ready Time"}
      </Typography>
      <Box sx={{ mb: 1 }}>
        <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
          Start time <span style={{ color: "red" }}>*</span>
        </Typography>
      </Box>
      <DateTimePicker
        value={safeFormat(
          stops?.[stopIndex]?.isAppointmentPickup
            ? stops?.[stopIndex]?.pickUpTimeStart
            : stops?.[stopIndex]?.readyTimeStart,
        )}
        name={
          stops?.[stopIndex]?.isAppointmentPickup
            ? "pickUpTimeStart"
            : "readyTimeStart"
        }
        placeholder="MM-DD-YY @ HH:MM"
        variant="outlined"
        disabled
        disablePast={true}
      />
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
          End time <span style={{ color: "red" }}>*</span>
        </Typography>
      </Box>
      <DateTimePicker
        value={safeFormat(
          stops?.[stopIndex]?.isAppointmentPickup
            ? stops?.[stopIndex]?.pickUpTimeEnd
            : stops?.[stopIndex]?.readyTimeEnd,
        )}
        name={
          stops?.[stopIndex]?.isAppointmentPickup
            ? "pickUpTimeEnd"
            : "readyTimeEnd"
        }
        placeholder="MM-DD-YY @ HH:MM"
        variant="outlined"
        disablePast={true}
        disabled
      />
      <FormControlLabel
        control={
          <Checkbox
            name="makeAppointment"
            checked={stops?.[stopIndex]?.isAppointmentPickup || false}
            size="small"
            disabled={true}
            sx={{ padding: 0.3, ml: 1 }}
          />
        }
        label="Make this an appointment"
        sx={{ mt: 1 }}
      />
      {stops?.[stopIndex]?.makeAppointment && (
        <FormControlLabel
          control={
            <Checkbox
              name="isAppointmentConfirmPickup"
              checked={stops?.[stopIndex]?.isAppointmentConfirmPickup || false}
              size="small"
              disabled={true}
              sx={{ padding: 0.3, ml: 1 }}
            />
          }
          label="Confirm pickup appointment"
          sx={{ mt: 1, display: "block" }}
        />
      )}
    </Grid>
  );

  // Render consignee section (dynamic based on orderType)
  const renderConsigneeSection = () => {
    const isSwapped = values?.orderType === "single_consignee";
    const sectionData = isSwapped ? "shipper" : "consignee";
    const addressPath = isSwapped ? "address" : "addresses";

    return (
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {getConsigneeSectionTitle()}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Quick search
        </Typography>
        <AddressAutocomplete
          customers={isSwapped ? shipperCustomers : consigneeCustomers}
          values={values}
          handleNestedChange={handleNestedChange}
          handleAddressChange={handleAddressChange}
          setFieldValue={setFieldValue}
          fieldName={sectionData}
        />
        <TextField
          required
          fullWidth
          name={
            isSwapped
              ? `shipper.name`
              : `orderStops[${stopIndex}].consignee.name`
          }
          label="Company name"
          value={
            getConsigneeSectionData("name") ||
            getConsigneeSectionData("company") ||
            ""
          }
          onChange={(e) => {
            if (isSwapped) {
              handleNestedChange("shipper", "name", e.target.value);
            } else {
              handleNestedChange("consignee", "name", e.target.value);
            }
          }}
          placeholder="Company name"
          margin="normal"
          size="small"
          variant="outlined"
          sx={{ background: "#fff" }}
          error={touched?.consignee?.name && Boolean(errors?.consignee?.name)}
          helperText={touched?.consignee?.name && errors?.consignee?.name}
        />
        <TextField
          required
          fullWidth
          name={
            isSwapped
              ? `orderStops[${stopIndex}].shipper.addresses.address`
              : `orderStops[${stopIndex}].consignee.addresses.address`
          }
          label="Address1"
          value={getConsigneeSectionData(`${addressPath}.address`) || ""}
          onChange={(e) => {
            if (isSwapped) {
              handleAddressChange("shipper", "address", e.target.value);
            } else {
              handleAddressChange("consignee", "address", e.target.value);
            }
          }}
          margin="normal"
          size="small"
          variant="outlined"
          sx={{ background: "#fff" }}
          error={
            touched?.consignee?.addresses?.address &&
            Boolean(errors?.consignee?.addresses?.address)
          }
          helperText={
            touched?.consignee?.addresses?.address &&
            errors?.consignee?.addresses?.address
          }
        />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              required
              fullWidth
              name={
                isSwapped
                  ? `orderStops[${stopIndex}].shipper.addresses.city`
                  : "consignee.addresses.city"
              }
              label="City"
              value={getConsigneeSectionData(`${addressPath}.city`) || ""}
              onChange={(e) => {
                if (isSwapped) {
                  handleAddressChange("shipper", "city", e.target.value);
                } else {
                  handleAddressChange("consignee", "city", e.target.value);
                }
              }}
              placeholder="City"
              margin="normal"
              size="small"
              variant="outlined"
              sx={{ background: "#fff" }}
              error={
                touched?.consignee?.addresses?.city &&
                Boolean(errors?.consignee?.addresses?.city)
              }
              helperText={
                touched?.consignee?.addresses?.city &&
                errors?.consignee?.addresses?.city
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              fullWidth
              name={
                isSwapped
                  ? `orderStops[${stopIndex}].shipper.addresses.state`
                  : "consignee.addresses.state"
              }
              label="State"
              value={getConsigneeSectionData(`${addressPath}.state`) || ""}
              onChange={(e) => {
                if (isSwapped) {
                  handleAddressChange("shipper", "state", e.target.value);
                } else {
                  handleAddressChange("consignee", "state", e.target.value);
                }
              }}
              placeholder="State"
              margin="normal"
              size="small"
              variant="outlined"
              sx={{ background: "#fff" }}
              error={
                touched?.consignee?.addresses?.state &&
                Boolean(errors?.consignee?.addresses?.state)
              }
              helperText={
                touched?.consignee?.addresses?.state &&
                errors?.consignee?.addresses?.state
              }
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              required
              fullWidth
              name={
                isSwapped
                  ? `orderStops[${stopIndex}].shipper.addresses.country`
                  : "consignee.addresses.country"
              }
              label="Country"
              value={getConsigneeSectionData(`${addressPath}.country`) || ""}
              onChange={(e) => {
                if (isSwapped) {
                  handleAddressChange("shipper", "country", e.target.value);
                } else {
                  handleAddressChange("consignee", "country", e.target.value);
                }
              }}
              placeholder="Country"
              margin="normal"
              size="small"
              variant="outlined"
              sx={{ background: "#fff" }}
              error={
                touched?.consignee?.addresses?.country &&
                Boolean(errors?.consignee?.addresses?.country)
              }
              helperText={
                touched?.consignee?.addresses?.country &&
                errors?.consignee?.addresses?.country
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              fullWidth
              name={
                isSwapped
                  ? `orderStops[${stopIndex}].shipper.addresses.postal`
                  : "consignee.addresses.postal"
              }
              label="ZIP code"
              value={getConsigneeSectionData(`${addressPath}.postal`) || ""}
              onChange={(e) => {
                if (isSwapped) {
                  handleAddressChange("shipper", "postal", e.target.value);
                } else {
                  handleAddressChange("consignee", "postal", e.target.value);
                }
              }}
              placeholder="ZIP code"
              margin="normal"
              size="small"
              variant="outlined"
              sx={{ background: "#fff" }}
              error={
                touched?.consignee?.addresses?.postal &&
                Boolean(errors?.consignee?.addresses?.postal)
              }
              helperText={
                touched?.consignee?.addresses?.postal &&
                errors?.consignee?.addresses?.postal
              }
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              name={
                isSwapped
                  ? `orderStops[${stopIndex}].shipper.addresses.latitude`
                  : "consignee.addresses.latitude"
              }
              label="Latitude"
              required
              value={getConsigneeSectionData(`${addressPath}.latitude`) || ""}
              onChange={(e) => {
                if (isSwapped) {
                  handleAddressChange("shipper", "latitude", e.target.value);
                } else {
                  handleAddressChange("consignee", "latitude", e.target.value);
                }
              }}
              margin="normal"
              size="small"
              variant="outlined"
              sx={{ background: "#fff" }}
              error={
                touched?.consignee?.addresses?.latitude &&
                Boolean(errors?.consignee?.addresses?.latitude)
              }
              helperText={
                touched?.consignee?.addresses?.latitude &&
                errors?.consignee?.addresses?.latitude
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              name={
                isSwapped
                  ? `orderStops[${stopIndex}].shipper.addresses.longitude`
                  : "consignee.addresses.longitude"
              }
              label="Longitude"
              required
              value={getConsigneeSectionData(`${addressPath}.longitude`) || ""}
              onChange={(e) => {
                if (isSwapped) {
                  handleAddressChange("shipper", "longitude", e.target.value);
                } else {
                  handleAddressChange("consignee", "longitude", e.target.value);
                }
              }}
              margin="normal"
              size="small"
              variant="outlined"
              sx={{ background: "#fff" }}
              error={
                touched?.consignee?.addresses?.longitude &&
                Boolean(errors?.consignee?.addresses?.longitude)
              }
              helperText={
                touched?.consignee?.addresses?.longitude &&
                errors?.consignee?.addresses?.longitude
              }
            />
          </Grid>
        </Grid>
        <TextField
          fullWidth
          name={isSwapped ? "shipper.notes" : "consignee.notes"}
          label={`${getConsigneeSectionTitle()} notes`}
          value={getConsigneeSectionData("notes") || ""}
          onChange={(e) => {
            if (isSwapped) {
              handleNestedChange("shipper", "notes", e.target.value);
            } else {
              handleNestedChange("consignee", "notes", e.target.value);
            }
          }}
          multiline
          rows={4}
          margin="normal"
          size="small"
          variant="outlined"
          sx={{ background: "#fff" }}
          error={touched?.consignee?.notes && Boolean(errors?.consignee?.notes)}
          helperText={touched?.consignee?.notes && errors?.consignee?.notes}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <InfoOutlined color="disabled" fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Grid container spacing={2} sx={{ mt: "0px" }}>
          <Grid item xs={6}>
            <CustomSelect
              label="Opening time"
              name={isSwapped ? "shipper.openingTime" : "consignee.openingTime"}
              value={getConsigneeSectionData("openingTime") || ""}
              onChange={(field, newValue) => {
                const value = newValue?.target?.value ?? newValue;
                if (isSwapped) {
                  handleNestedChange("shipper", "openingTime", value);
                } else {
                  handleNestedChange("consignee", "openingTime", value);
                }
              }}
              options={TIME_OPTIONS.map((time) => ({
                id: time,
                value: time,
                label: time,
              }))}
              size="small"
              sx={{ background: "#fff" }}
              error={
                touched?.consignee?.openingTime &&
                Boolean(errors?.consignee?.openingTime)
              }
              errorMessage={
                touched?.consignee?.openingTime &&
                errors?.consignee?.openingTime
              }
            />
          </Grid>
          <Grid item xs={6}>
            <CustomSelect
              label="Closing time"
              name={isSwapped ? "shipper.closingTime" : "consignee.closingTime"}
              value={getConsigneeSectionData("closingTime") || ""}
              onChange={(field, newValue) => {
                const value = newValue?.target?.value ?? newValue;
                if (isSwapped) {
                  handleNestedChange("shipper", "closingTime", value);
                } else {
                  handleNestedChange("consignee", "closingTime", value);
                }
              }}
              options={TIME_OPTIONS.map((time) => ({
                id: time,
                value: time,
                label: time,
              }))}
              size="small"
              sx={{ background: "#fff" }}
              error={
                touched?.consignee?.closingTime &&
                Boolean(errors?.consignee?.closingTime)
              }
              errorMessage={
                touched?.consignee?.closingTime &&
                errors?.consignee?.closingTime
              }
            />
          </Grid>
        </Grid>
        <TextField
          fullWidth
          name={isSwapped ? "shipper.contactName" : "consignee.contactName"}
          label="Contact name"
          value={getConsigneeSectionData("contactName") || ""}
          onChange={(e) => {
            if (isSwapped) {
              handleNestedChange("shipper", "contactName", e.target.value);
            } else {
              handleNestedChange("consignee", "contactName", e.target.value);
            }
          }}
          placeholder="Full name"
          margin="normal"
          size="small"
          variant="outlined"
          sx={{ background: "#fff" }}
          error={
            touched?.consignee?.contactName &&
            Boolean(errors?.consignee?.contactName)
          }
          helperText={
            touched?.consignee?.contactName && errors?.consignee?.contactName
          }
        />
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <TextField
              fullWidth
              name={
                isSwapped ? "shipper.contactPhone" : "consignee.contactPhone"
              }
              label="Phone"
              value={
                getConsigneeSectionData("contactPhone") ||
                getConsigneeSectionData("phone") ||
                ""
              }
              onChange={(e) => {
                if (isSwapped) {
                  handleNestedChange("shipper", "contactPhone", e.target.value);
                } else {
                  handleNestedChange(
                    "consignee",
                    "contactPhone",
                    e.target.value,
                  );
                }
              }}
              placeholder="(000) 000-0000"
              size="small"
              variant="outlined"
              sx={{ background: "#fff" }}
              error={
                touched?.consignee?.contactPhone &&
                Boolean(errors?.consignee?.contactPhone)
              }
              helperText={
                touched?.consignee?.contactPhone &&
                errors?.consignee?.contactPhone
              }
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              name={isSwapped ? "shipper.contactExt" : "consignee.contactExt"}
              label="Ext."
              value={getConsigneeSectionData("contactExt") || ""}
              onChange={(e) => {
                if (isSwapped) {
                  handleNestedChange("shipper", "contactExt", e.target.value);
                } else {
                  handleNestedChange("consignee", "contactExt", e.target.value);
                }
              }}
              placeholder="Ext."
              size="small"
              variant="outlined"
              sx={{ background: "#fff" }}
              error={
                touched?.consignee?.contactExt &&
                Boolean(errors?.consignee?.contactExt)
              }
              helperText={
                touched?.consignee?.contactExt && errors?.consignee?.contactExt
              }
            />
          </Grid>
        </Grid>
        <TextField
          fullWidth
          name={isSwapped ? "shipper.contactEmail" : "consignee.contactEmail"}
          label="Contact email"
          value={getConsigneeSectionData("contactEmail") || ""}
          onChange={(e) => {
            if (isSwapped) {
              handleNestedChange("shipper", "contactEmail", e.target.value);
            } else {
              handleNestedChange("consignee", "contactEmail", e.target.value);
            }
          }}
          placeholder="Contact email"
          margin="normal"
          size="small"
          variant="outlined"
          sx={{ background: "#fff" }}
          error={
            touched?.consignee?.contactEmail &&
            Boolean(errors?.consignee?.contactEmail)
          }
          helperText={
            touched?.consignee?.contactEmail && errors?.consignee?.contactEmail
          }
        />
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          gutterBottom
          sx={{ mt: 3 }}
        >
          {values.isAppointmentDelivery
            ? "Delivery time"
            : "Requested delivery"}
        </Typography>
        <DateTimePicker
          variant="outlined"
          label={
            values.isAppointmentDelivery
              ? "Delivery start time"
              : "Requested start time"
          }
          value={
            values.isAppointmentDelivery
              ? values.deliveryTimeStart || stops[stopIndex]?.deliveryTimeStart
                ? format(
                    new Date(
                      values.deliveryTimeStart ||
                        stops[stopIndex]?.deliveryTimeStart,
                    ),
                    "MM/dd/yyyy hh:mm a",
                  )
                : ""
              : values.requestedTimeStart ||
                  stops[stopIndex]?.requestedTimeStart
                ? format(
                    new Date(
                      values.requestedTimeStart ||
                        stops[stopIndex]?.requestedTimeStart,
                    ),
                    "MM/dd/yyyy hh:mm a",
                  )
                : ""
          }
          onChange={(value) => {
            const field = values.isAppointmentDelivery
              ? "deliveryTimeStart"
              : "requestedTimeStart";
            setFieldValue(`orderStops[${stopIndex}].${field}`, value);
          }}
          name={
            values.isAppointmentDelivery
              ? "deliveryTimeStart"
              : "requestedTimeStart"
          }
          error={
            values.isAppointmentDelivery
              ? touched?.deliveryTimeStart && Boolean(errors?.deliveryTimeStart)
              : touched?.requestedTimeStart &&
                Boolean(errors?.requestedTimeStart)
          }
          errorMessage={
            values.isAppointmentDelivery
              ? touched?.deliveryTimeStart && errors?.deliveryTimeStart
              : touched?.requestedTimeStart && errors?.requestedTimeStart
          }
          disablePast
          shouldDisableDate={isToday}
        />
        <DateTimePicker
          variant="outlined"
          label={
            values.isAppointmentDelivery
              ? "Delivery end time"
              : "Requested end time"
          }
          value={
            values.isAppointmentDelivery
              ? values.deliveryTimeEnd || stops[stopIndex]?.deliveryTimeEnd
                ? format(
                    new Date(
                      values.deliveryTimeEnd ||
                        stops[stopIndex]?.deliveryTimeEnd,
                    ),
                    "MM/dd/yyyy hh:mm a",
                  )
                : ""
              : values.requestedTimeEnd || stops[stopIndex]?.requestedTimeEnd
                ? format(
                    new Date(
                      values.requestedTimeEnd ||
                        stops[stopIndex]?.requestedTimeEnd,
                    ),
                    "MM/dd/yyyy hh:mm a",
                  )
                : ""
          }
          onChange={(value) => {
            const field = values.isAppointmentDelivery
              ? "deliveryTimeEnd"
              : "requestedTimeEnd";
            setFieldValue(`orderStops[${stopIndex}].${field}`, value);
          }}
          name={
            values.isAppointmentDelivery
              ? "deliveryTimeEnd"
              : "requestedTimeEnd"
          }
          sx={{ mt: 2 }}
          error={
            values.isAppointmentDelivery
              ? touched?.deliveryTimeEnd && Boolean(errors?.deliveryTimeEnd)
              : touched?.requestedTimeEnd && Boolean(errors?.requestedTimeEnd)
          }
          errorMessage={
            values.isAppointmentDelivery
              ? touched?.deliveryTimeEnd && errors?.deliveryTimeEnd
              : touched?.requestedTimeEnd && errors?.requestedTimeEnd
          }
          disablePast
          shouldDisableDate={isToday}
        />
        <FormControlLabel
          control={
            <Checkbox
              name="isAppointmentDelivery"
              checked={
                values?.orderStops?.[stopIndex]?.isAppointmentDelivery || false
              }
              onChange={(e) =>
                handleDeliveryAppointmentChange(e.target.checked)
              }
              size="small"
              sx={{ my: 0, py: 0 }}
            />
          }
          label="Make this an appointment"
          sx={{ mt: 1 }}
        />
        {values?.orderStops?.[stopIndex]?.isAppointmentDelivery && (
          <FormControlLabel
            control={
              <Checkbox
                name="isAppointmentConfirmDelivery"
                checked={
                  values?.orderStops?.[stopIndex]
                    ?.isAppointmentConfirmDelivery || false
                }
                onChange={(e) =>
                  setFieldValue(
                    `orderStops[${stopIndex}].isAppointmentConfirmDelivery`,
                    e.target.checked,
                  )
                }
                size="small"
                sx={{ my: 0, py: 0 }}
              />
            }
            label="Appointment confirmed"
            sx={{ mt: 1 }}
          />
        )}
      </Grid>
    );
  };

  // Render sequence section (dynamic based on orderType)
  const renderSequenceSection = () => {
    return (
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Shipper
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Quick search
        </Typography>
        <AddressAutocomplete
          customers={shipperCustomers}
          values={values}
          handleNestedChange={handleNestedChange}
          handleAddressChange={handleAddressChange}
          setFieldValue={setFieldValue}
          fieldName={"shipper"}
        />
        <TextField
          required
          fullWidth
          name={`shipper.name`}
          label="Company name"
          value={
            getShipperSectionData("name") ||
            getShipperSectionData("company") ||
            ""
          }
          onChange={(e) => {
            handleNestedChange("shipper", "name", e.target.value);
          }}
          placeholder="Company name"
          margin="normal"
          size="small"
          variant="outlined"
          sx={{ background: "#fff" }}
          error={
            touched?.orderStops?.[stopIndex]?.shipper?.name &&
            Boolean(errors?.orderStops?.[stopIndex]?.shipper?.name)
          }
          helperText={
            touched?.orderStops?.[stopIndex]?.shipper?.name &&
            errors?.orderStops?.[stopIndex]?.shipper?.name
          }
        />
        <TextField
          required
          fullWidth
          name={`orderStops[${stopIndex}].shipper.addresses.address`}
          label="Address1"
          value={getShipperSectionData("addresses.address") || ""}
          onChange={(e) => {
            handleAddressChange("shipper", "address", e.target.value);
          }}
          margin="normal"
          size="small"
          variant="outlined"
          sx={{ background: "#fff" }}
          error={
            touched?.orderStops?.[stopIndex]?.shipper?.address?.address &&
            Boolean(errors?.orderStops?.[stopIndex]?.shipper?.address?.address)
          }
          helperText={
            touched?.orderStops?.[stopIndex]?.shipper?.address?.address &&
            errors?.orderStops?.[stopIndex]?.shipper?.address?.address
          }
        />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              required
              fullWidth
              name={`orderStops[${stopIndex}].shipper.addresses.city`}
              label="City"
              value={getShipperSectionData("addresses.city") || ""}
              onChange={(e) => {
                handleAddressChange("shipper", "city", e.target.value);
              }}
              placeholder="City"
              margin="normal"
              size="small"
              variant="outlined"
              sx={{ background: "#fff" }}
              error={
                touched?.orderStops?.[stopIndex]?.shipper?.address?.city &&
                Boolean(errors?.orderStops?.[stopIndex]?.shipper?.address?.city)
              }
              helperText={
                touched?.orderStops?.[stopIndex]?.shipper?.address?.city &&
                errors?.orderStops?.[stopIndex]?.shipper?.address?.city
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              fullWidth
              name={`orderStops[${stopIndex}].shipper.addresses.state`}
              label="State"
              value={getShipperSectionData("addresses.state") || ""}
              onChange={(e) => {
                handleAddressChange("shipper", "state", e.target.value);
              }}
              placeholder="State"
              margin="normal"
              size="small"
              variant="outlined"
              sx={{ background: "#fff" }}
              error={
                touched?.orderStops?.[stopIndex]?.shipper?.address?.state &&
                Boolean(
                  errors?.orderStops?.[stopIndex]?.shipper?.address?.state,
                )
              }
              helperText={
                touched?.orderStops?.[stopIndex]?.shipper?.address?.state &&
                errors?.orderStops?.[stopIndex]?.shipper?.address?.state
              }
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              required
              fullWidth
              name={`orderStops[${stopIndex}].shipper.addresses.country`}
              label="Country"
              value={getShipperSectionData("addresses.country") || ""}
              onChange={(e) => {
                handleAddressChange("shipper", "country", e.target.value);
              }}
              placeholder="Country"
              margin="normal"
              size="small"
              variant="outlined"
              sx={{ background: "#fff" }}
              error={
                touched?.orderStops?.[stopIndex]?.shipper?.address?.country &&
                Boolean(
                  errors?.orderStops?.[stopIndex]?.shipper?.address?.country,
                )
              }
              helperText={
                touched?.orderStops?.[stopIndex]?.shipper?.address?.country &&
                errors?.orderStops?.[stopIndex]?.shipper?.address?.country
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              fullWidth
              name={`orderStops[${stopIndex}].shipper.addresses.postal`}
              label="ZIP code"
              value={getShipperSectionData("addresses.postal") || ""}
              onChange={(e) => {
                handleAddressChange("shipper", "postal", e.target.value);
              }}
              placeholder="ZIP code"
              margin="normal"
              size="small"
              variant="outlined"
              sx={{ background: "#fff" }}
              error={
                touched?.orderStops?.[stopIndex]?.shipper?.address?.postal &&
                Boolean(
                  errors?.orderStops?.[stopIndex]?.shipper?.address?.postal,
                )
              }
              helperText={
                touched?.orderStops?.[stopIndex]?.shipper?.address?.postal &&
                errors?.orderStops?.[stopIndex]?.shipper?.address?.postal
              }
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              name={`orderStops[${stopIndex}].shipper.addresses.latitude`}
              label="Latitude"
              required
              value={getShipperSectionData("addresses.latitude") || ""}
              onChange={(e) => {
                handleAddressChange("shipper", "latitude", e.target.value);
              }}
              margin="normal"
              size="small"
              variant="outlined"
              sx={{ background: "#fff" }}
              error={
                touched?.orderStops?.[stopIndex]?.shipper?.address?.latitude &&
                Boolean(
                  errors?.orderStops?.[stopIndex]?.shipper?.address?.latitude,
                )
              }
              helperText={
                touched?.orderStops?.[stopIndex]?.shipper?.address?.latitude &&
                errors?.orderStops?.[stopIndex]?.shipper?.address?.latitude
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              name={`orderStops[${stopIndex}].shipper.addresses.longitude`}
              label="Longitude"
              required
              value={getShipperSectionData("addresses.longitude") || ""}
              onChange={(e) => {
                handleAddressChange("shipper", "longitude", e.target.value);
              }}
              margin="normal"
              size="small"
              variant="outlined"
              sx={{ background: "#fff" }}
              error={
                touched?.orderStops?.[stopIndex]?.shipper?.address?.longitude &&
                Boolean(
                  errors?.orderStops?.[stopIndex]?.shipper?.address?.longitude,
                )
              }
              helperText={
                touched?.orderStops?.[stopIndex]?.shipper?.address?.longitude &&
                errors?.orderStops?.[stopIndex]?.shipper?.address?.longitude
              }
            />
          </Grid>
        </Grid>
        <TextField
          fullWidth
          name={"shipper.notes"}
          label="Shipper notes"
          value={getShipperSectionData("notes") || ""}
          onChange={(e) => {
            handleNestedChange("shipper", "notes", e.target.value);
          }}
          multiline
          rows={4}
          margin="normal"
          size="small"
          variant="outlined"
          sx={{ background: "#fff" }}
          error={touched?.shipper?.notes && Boolean(errors?.shipper?.notes)}
          helperText={touched?.shipper?.notes && errors?.shipper?.notes}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <InfoOutlined color="disabled" fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Grid container spacing={2} sx={{ mt: "0px" }}>
          <Grid item xs={6}>
            <CustomSelect
              label="Opening time"
              name={"shipper.openingTime"}
              value={getShipperSectionData("openingTime") || ""}
              onChange={(field, newValue) => {
                const value = newValue?.target?.value ?? newValue;

                handleNestedChange("shipper", "openingTime", value);
              }}
              options={TIME_OPTIONS.map((time) => ({
                id: time,
                value: time,
                label: time,
              }))}
              size="small"
              sx={{ background: "#fff" }}
              error={
                touched?.orderStops?.[stopIndex]?.shipper?.openingTime &&
                Boolean(errors?.orderStops?.[stopIndex]?.shipper?.openingTime)
              }
              errorMessage={
                touched?.orderStops?.[stopIndex]?.shipper?.openingTime &&
                errors?.orderStops?.[stopIndex]?.shipper?.openingTime
              }
            />
          </Grid>
          <Grid item xs={6}>
            <CustomSelect
              label="Closing time"
              name={"shipper.closingTime"}
              value={getShipperSectionData("closingTime") || ""}
              onChange={(field, newValue) => {
                const value = newValue?.target?.value ?? newValue;

                handleNestedChange("shipper", "closingTime", value);
              }}
              options={TIME_OPTIONS.map((time) => ({
                id: time,
                value: time,
                label: time,
              }))}
              size="small"
              sx={{ background: "#fff" }}
              error={
                touched?.orderStops?.[stopIndex]?.shipper?.closingTime &&
                Boolean(errors?.orderStops?.[stopIndex]?.shipper?.closingTime)
              }
              errorMessage={
                touched?.orderStops?.[stopIndex]?.shipper?.closingTime &&
                errors?.orderStops?.[stopIndex]?.shipper?.closingTime
              }
            />
          </Grid>
        </Grid>
        <TextField
          fullWidth
          name={"shipper.contactName"}
          label="Contact name"
          value={getShipperSectionData("contactName") || ""}
          onChange={(e) => {
            handleNestedChange("shipper", "contactName", e.target.value);
          }}
          placeholder="Full name"
          margin="normal"
          size="small"
          variant="outlined"
          sx={{ background: "#fff" }}
          error={
            touched?.orderStops?.[stopIndex]?.shipper?.contactName &&
            Boolean(errors?.orderStops?.[stopIndex]?.shipper?.contactName)
          }
          helperText={
            touched?.orderStops?.[stopIndex]?.shipper?.contactName &&
            errors?.orderStops?.[stopIndex]?.shipper?.contactName
          }
        />
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <TextField
              fullWidth
              name={"shipper.contactPhone"}
              label="Phone"
              value={
                getShipperSectionData("contactPhone") ||
                getShipperSectionData("phone") ||
                ""
              }
              onChange={(e) => {
                handleNestedChange("shipper", "contactPhone", e.target.value);
              }}
              placeholder="(000) 000-0000"
              size="small"
              variant="outlined"
              sx={{ background: "#fff" }}
              error={
                touched?.orderStops?.[stopIndex]?.shipper?.contactPhone &&
                Boolean(errors?.orderStops?.[stopIndex]?.shipper?.contactPhone)
              }
              helperText={
                touched?.orderStops?.[stopIndex]?.shipper?.contactPhone &&
                errors?.orderStops?.[stopIndex]?.shipper?.contactPhone
              }
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              name={"shipper.contactExt"}
              label="Ext."
              value={getShipperSectionData("contactExt") || ""}
              onChange={(e) => {
                handleNestedChange("shipper", "contactExt", e.target.value);
              }}
              placeholder="Ext."
              size="small"
              variant="outlined"
              sx={{ background: "#fff" }}
              error={
                touched?.orderStops?.[stopIndex]?.shipper?.contactExt &&
                Boolean(errors?.orderStops?.[stopIndex]?.shipper?.contactExt)
              }
              helperText={
                touched?.orderStops?.[stopIndex]?.shipper?.contactExt &&
                errors?.orderStops?.[stopIndex]?.shipper?.contactExt
              }
            />
          </Grid>
        </Grid>
        <TextField
          fullWidth
          name={"shipper.contactEmail"}
          label="Contact email"
          value={getShipperSectionData("contactEmail") || ""}
          onChange={(e) => {
            handleNestedChange("shipper", "contactEmail", e.target.value);
          }}
          placeholder="Contact email"
          margin="normal"
          size="small"
          variant="outlined"
          sx={{ background: "#fff" }}
          error={
            touched?.orderStops?.[stopIndex]?.shipper?.contactEmail &&
            Boolean(errors?.orderStops?.[stopIndex]?.shipper?.contactEmail)
          }
          helperText={
            touched?.orderStops?.[stopIndex]?.shipper?.contactEmail &&
            errors?.orderStops?.[stopIndex]?.shipper?.contactEmail
          }
        />
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          gutterBottom
          sx={{ mt: 3 }}
        >
          {values?.orderStops?.[stopIndex]?.isAppointmentPickup
            ? "Pickup time"
            : "Ready time"}
        </Typography>
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
            Start time <span style={{ color: "red" }}>*</span>
          </Typography>
        </Box>
        <DateTimePicker
          onChange={(value) => {
            const field = values.orderStops?.[stopIndex]?.isAppointmentPickup
              ? "pickUpTimeStart"
              : "readyTimeStart";
            const endField = values.orderStops?.[stopIndex]?.isAppointmentPickup
              ? "pickUpTimeEnd"
              : "readyTimeEnd";
            setFieldValue(`orderStops[${stopIndex}].${field}`, value);
            setFieldValue(`orderStops[${stopIndex}].${endField}`, value);
            setTouched({
              ...touched,
              [`orderStops[${stopIndex}].${field}`]: true,
              [`orderStops[${stopIndex}].${endField}`]: true,
            });
          }}
          value={safeFormat(
            values?.orderStops?.[stopIndex]?.isAppointmentPickup
              ? values?.orderStops?.[stopIndex]?.pickUpTimeStart ||
                  stops?.[stopIndex]?.pickUpTimeStart
              : values?.orderStops?.[stopIndex]?.readyTimeStart ||
                  stops?.[stopIndex]?.readyTimeStart,
          )}
          name={
            values?.orderStops[stopIndex]?.isAppointmentPickup
              ? "pickUpTimeStart"
              : "readyTimeStart"
          }
          placeholder="MM-DD-YY @ HH:MM"
          variant="outlined"
          disablePast={true}
        />
        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
            End time <span style={{ color: "red" }}>*</span>
          </Typography>
        </Box>
        <DateTimePicker
          value={safeFormat(
            values?.orderStops?.[stopIndex]?.isAppointmentPickup
              ? values?.orderStops?.[stopIndex]?.pickUpTimeEnd ||
                  stops?.[stopIndex]?.pickUpTimeEnd
              : values?.orderStops?.[stopIndex]?.readyTimeEnd ||
                  stops?.[stopIndex]?.readyTimeEnd,
          )}
          onChange={(value) => {
            const field = values.orderStops?.[stopIndex]?.isAppointmentPickup
              ? "pickUpTimeEnd"
              : "readyTimeEnd";
            setFieldValue(`orderStops[${stopIndex}].${field}`, value);
            setTouched({
              ...touched,
              [`orderStops[${stopIndex}].${field}`]: true,
            });
          }}
          name={
            values?.orderStops?.[stopIndex]?.isAppointmentPickup
              ? "pickUpTimeEnd"
              : "readyTimeEnd"
          }
          placeholder="MM-DD-YY @ HH:MM"
          variant="outlined"
          disablePast={true}
        />

        <FormControlLabel
          control={
            <Checkbox
              name="isAppointmentDelivery"
              checked={
                values?.orderStops?.[stopIndex]?.isAppointmentDelivery || false
              }
              onChange={(e) =>
                handleDeliveryAppointmentChange(e.target.checked)
              }
              size="small"
              sx={{ my: 0, py: 0 }}
            />
          }
          label="Make this an appointment"
          sx={{ mt: 1 }}
        />
        {values?.orderStops?.[stopIndex]?.isAppointmentDelivery && (
          <FormControlLabel
            control={
              <Checkbox
                name="isAppointmentConfirmDelivery"
                checked={
                  values?.orderStops?.[stopIndex]
                    ?.isAppointmentConfirmDelivery || false
                }
                onChange={(e) =>
                  setFieldValue(
                    `orderStops[${stopIndex}].isAppointmentConfirmDelivery`,
                    e.target.checked,
                  )
                }
                size="small"
                sx={{ my: 0, py: 0 }}
              />
            }
            label="Appointment confirmed"
            sx={{ mt: 1 }}
          />
        )}
      </Grid>
    );
  };

  // Render billing party section
  const renderBillingPartySection = () => (
    <Grid item xs={12} md={4}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          Billing party
        </Typography>
        <Button
          startIcon={<Edit />}
          variant="outlined"
          size="small"
          sx={{ textTransform: "none" }}
          onClick={handleOpenBillingModal}
        >
          Edit
        </Button>
      </Box>
      <Dialog
        open={openBillingModal}
        onClose={handleCloseBillingModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Editing billing address"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Modifying the billing party will change the related customer for
            this order and causing a new quote to be generated.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBillingModal} color="primary">
            Cancel
          </Button>
          <Button onClick={handleContinueBillingEdit} color="primary" autoFocus>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      {!showBillingForm ? (
        <>
          <Typography variant="body1" gutterBottom>
            {getBillingPartyValue("name")}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {getBillingPartyValue("addresses.address")}
          </Typography>
          <Typography variant="body2" color="primary" gutterBottom>
            {`${getBillingPartyValue("addresses.city")}, ${getBillingPartyValue(
              "addresses.state",
            )}, ${getBillingPartyValue(
              "addresses.country",
            )} ${getBillingPartyValue("addresses.postal")}`}
          </Typography>
        </>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Quick search
          </Typography>
          <AddressAutocomplete
            customers={billingCustomers}
            values={values}
            handleNestedChange={handleNestedChange}
            handleAddressChange={handleAddressChange}
            setFieldValue={setFieldValue}
            fieldName="billingParty"
          />
          <TextField
            required
            fullWidth
            name="billingParty.name"
            label="Company name"
            value={getBillingPartyValue("name")}
            onChange={(e) =>
              handleNestedChange("billingParty", "name", e.target.value)
            }
            margin="normal"
            size="small"
            variant="outlined"
            sx={{ background: "#fff" }}
            error={
              touched?.billingParty?.name && Boolean(errors?.billingParty?.name)
            }
            helperText={
              touched?.billingParty?.name && errors?.billingParty?.name
            }
          />
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setShowBillingForm(false)}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        gutterBottom
        sx={{ mt: 4 }}
      >
        Additional information
      </Typography>
      <TextField
        fullWidth
        name="customsBroker"
        label="Customs broker"
        value={getStopValue("customsBroker")}
        onChange={handleChange}
        placeholder="Customs broker"
        margin="normal"
        size="small"
        variant="outlined"
        sx={{ background: "#fff" }}
        error={touched?.customsBroker && Boolean(errors?.customsBroker)}
        helperText={touched?.customsBroker && errors?.customsBroker}
      />
      <TextField
        fullWidth
        name="portOfEntry"
        label="Port of entry"
        value={getStopValue("portOfEntry")}
        onChange={handleChange}
        placeholder="Port of entry"
        margin="normal"
        size="small"
        variant="outlined"
        sx={{ background: "#fff" }}
        error={touched?.portOfEntry && Boolean(errors?.portOfEntry)}
        helperText={touched?.portOfEntry && errors?.portOfEntry}
      />
      <TextField
        fullWidth
        name="declaredValue"
        label="Declared value"
        value={getStopValue("declaredValue")}
        onChange={handleChange}
        placeholder="0.00"
        margin="normal"
        size="small"
        variant="outlined"
        sx={{ background: "#fff", width: "100%" }}
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        error={touched?.declaredValue && Boolean(errors?.declaredValue)}
        helperText={touched?.declaredValue && errors?.declaredValue}
      />
      <TextField
        fullWidth
        name="poNumber"
        label="PO Number"
        value={getStopValue("poNumber")}
        onChange={handleChange}
        margin="normal"
        size="small"
        variant="outlined"
        sx={{ background: "#fff" }}
        error={touched?.poNumber && Boolean(errors?.poNumber)}
        helperText={touched?.poNumber && errors?.poNumber}
      />
      <TextField
        fullWidth
        name="refNumber"
        label="REF Number"
        value={getStopValue("refNumber")}
        onChange={handleChange}
        margin="normal"
        size="small"
        variant="outlined"
        sx={{ background: "#fff" }}
        error={touched?.refNumber && Boolean(errors?.refNumber)}
        helperText={touched?.refNumber && errors?.refNumber}
      />
      <TextField
        fullWidth
        name="tenderNumber"
        label="Load Tender Number"
        value={values.orderStops[stopIndex].tenderNumber || ""}
        onChange={handleChange}
        margin="normal"
        size="small"
        variant="outlined"
        sx={{ background: "#fff" }}
        error={touched?.tenderNumber && Boolean(errors?.tenderNumber)}
        helperText={touched?.tenderNumber && errors?.tenderNumber}
      />
      <TextField
        fullWidth
        name="customNumber"
        label="Custom Number"
        value={values.orderStops[stopIndex].customNumber || ""}
        onChange={handleChange}
        margin="normal"
        size="small"
        variant="outlined"
        sx={{ background: "#fff" }}
        error={touched?.customNumber && Boolean(errors?.customNumber)}
        helperText={touched?.customNumber && errors?.customNumber}
      />
    </Grid>
  );

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Grid container spacing={4}>
        {values?.orderType === "sequence"
          ? renderSequenceSection()
          : renderShipperSection()}
        {renderConsigneeSection()}
        {renderBillingPartySection()}
      </Grid>

      {/* <LegsTable
        order={values}
        selectedQuote={selectedQuote}
        formatDate={formatDate}
      /> */}

      {/* Special Instructions */}
      <Box
        sx={{
          mt: 2,
          borderRadius: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography variant="body2" gutterBottom fontWeight="bold">
          Special instructions
        </Typography>
        <TextField
          fullWidth
          name="instructions"
          value={values.orderStops[stopIndex].instructions || ""}
          onChange={handleChange}
          placeholder="ie. This shipment is fragile. Please handle with care."
          multiline
          rows={4}
          size="small"
          variant="outlined"
          sx={{ background: "#fff" }}
          error={touched?.instructions && Boolean(errors?.instructions)}
          helperText={touched?.instructions && errors?.instructions}
        />
      </Box>

      {/* Accessorials Section */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="body2" gutterBottom fontWeight="bold">
          Accessorials
        </Typography>
        {(() => {
          const stop = values?.orderStops?.[stopIndex] || {};
          // For multi-order stops, use orderStop-level accessorials (objects) for display
          const orderStopAccessorials = Array.isArray(stop?.accessorials)
            ? stop.accessorials
            : [];

          const filteredAccessorials = (accessorialsData || []).filter(
            (accessorial) => {
              if (!accessorial?.uuid) return false;
              const matchesSearch =
                !accessorialSearchTerm ||
                (accessorial.name || "")
                  .toLowerCase()
                  .includes(accessorialSearchTerm.toLowerCase());
              const isSelected = orderStopAccessorials.some(
                (acc) => acc.uuid === accessorial.uuid,
              );
              return matchesSearch && !isSelected;
            },
          );
          const selectedAccessorialObjects = orderStopAccessorials;

          return (
            <>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {selectedAccessorialObjects.map((accessorial) => (
                  <Chip
                    key={accessorial.uuid}
                    label={accessorial.name}
                    onDelete={() => handleRemoveAccessorial(accessorial.uuid)}
                    sx={{ m: 0.5 }}
                  />
                ))}
              </Box>
            </>
          );
        })()}

        <Box sx={{ position: "relative" }}>
          <Button
            startIcon={<Add />}
            variant="outlined"
            sx={{
              textTransform: "none",
              color: "primary.main",
              borderColor: "primary.main",
              borderRadius: 1,
              px: 3,
            }}
            onClick={() => setShowAccessorialDropdown(!showAccessorialDropdown)}
          >
            Add accessorial
          </Button>
          {showAccessorialDropdown && (
            <Paper
              elevation={3}
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                width: 350,
                maxHeight: 400,
                mt: 1,
                zIndex: 1000,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={{ p: 2, pb: 1 }}>
                <TextField
                  fullWidth
                  placeholder="Search accessorials"
                  variant="outlined"
                  size="small"
                  value={accessorialSearchTerm}
                  onChange={(e) => setAccessorialSearchTerm(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box
                sx={{
                  overflowY: "auto",
                  maxHeight: 300,
                  "&::-webkit-scrollbar": { width: "8px" },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#c1c1c1",
                    borderRadius: "4px",
                  },
                }}
              >
                {filteredAccessorials &&
                  filteredAccessorials.map((accessorial) => (
                    <Box
                      key={accessorial.uuid}
                      sx={{
                        p: 2,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                        borderBottom: "1px solid #f0f0f0",
                      }}
                      onClick={() => handleAddAccessorial(accessorial)}
                    >
                      <Typography variant="body2">
                        {accessorial.name}
                      </Typography>
                    </Box>
                  ))}
                {filteredAccessorials && filteredAccessorials.length === 0 && (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="body2" color="textSecondary">
                      No accessorials found
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          )}
        </Box>
      </Box>

      {/* Billable Miles */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="body2" gutterBottom fontWeight="bold">
          Billable miles
        </Typography>
        <TextField
          name="billingMiles"
          value={values?.orderStops[stopIndex]?.billingMiles || ""}
          onChange={handleChange}
          placeholder="0"
          margin="normal"
          size="small"
          variant="outlined"
          sx={{ width: "200px", background: "#fff" }}
          error={touched?.billingMiles && Boolean(errors?.billingMiles)}
          helperText={touched?.billingMiles && errors?.billingMiles}
        />
        <Button
          sx={{ textTransform: "none", color: "primary.main", ml: 2, mt: 2 }}
          onClick={calculateDistance}
          disabled={!hasValidCoordinates()}
        >
          Estimate with Google
        </Button>
      </Box>

      {/* Commodities Table */}
      <Box sx={{ mt: 4 }} ref={commoditiesRef}>
        <CommoditiesTable
          commodities={values?.orderStops?.[stopIndex]?.commodities || []}
          onAddCommodity={addCommodity}
          onRemoveCommodity={removeCommodity}
          onChangeCommodity={handleCommodityChange}
          serviceUuid={
            values?.orderStops[stopIndex]?.serviceUuids?.[0] ||
            values.orderStops[stopIndex]?.services?.[0]?.uuid ||
            ""
          }
          onChangeService={(value) => {
            setFieldValue(
              `orderStops[${stopIndex}].serviceUuids`,
              value ? [value] : [],
            );
          }}
          classes={[
            "50",
            "55",
            "60",
            "65",
            "70",
            "77.5",
            "85",
            "92.5",
            "100",
            "110",
            "125",
            "150",
            "175",
            "200",
            "250",
            "300",
            "400",
            "500",
          ]}
          showServiceType={true}
          required={true}
          touched={touched}
          errors={errors}
          orderId={orderId}
          values={values}
          stopIndex={stopIndex}
        />
      </Box>
    </Box>
  );
}
